import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import ConfirmationModal from '../shared/ConfirmationModal';
import NewconfirmationModal from '../shared/NewconfirmationModal';
import AllFailureModal from '../shared/AllFailureModal';

interface InterviewDetails {
  interviewId: string;
  candidateId: string;
  date: string;
  time: string;
  location: string;
  meetingLink?: string;
  additionalNotes?: string;
  status: string;
}

const InterviewReschedule: React.FC = () => {
  const [searchParams] = useSearchParams();
  const interviewId = searchParams.get("interviewId");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [interviewDetails, setInterviewDetails] = useState<InterviewDetails | null>(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [pendingValues, setPendingValues] = useState<any>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
const [showFailureModal, setShowFailureModal] = useState(false);
const [failureMessage, setFailureMessage] = useState("");
  
  const candidate = searchParams.get("candidate") ? JSON.parse(searchParams.get("candidate")!) : null;
  const job = searchParams.get("job") ? JSON.parse(searchParams.get("job")!) : null;

  useEffect(() => {
    if (interviewId) {
      fetchInterviewDetails();
    }
  }, [interviewId]);

  const fetchInterviewDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
         `${process.env.REACT_APP_API_URL}interviews/interviewDetails`
      );
      setInterviewDetails(response.data);
      formik.setValues({
        date: response.data.date,
        time: response.data.time,
        location: response.data.location,
        meetingLink: response.data.meetingLink || '',
        additionalNotes: response.data.additionalNotes || ''
      });
    } catch (error) {
      console.log(error);
      setFailureMessage("Failed to fetch interview details.");
      setShowFailureModal(true);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      date: "",
      time: "",
      location: "",
      meetingLink: "",
      additionalNotes: ""
    },
    validationSchema: Yup.object({
      date: Yup.string().required("Date is required"),
      time: Yup.string().required("Time is required"),
      location: Yup.string().required("Location is required")
    }),
    onSubmit: (values) => {
      setPendingValues(values);
      setModalMessage("Are you sure you want to reschedule the interview?");
      setModalShow(true);
    },
  });

  const handleConfirmation = async (confirm: boolean) => {
    setModalShow(false);
    if (confirm && pendingValues) {
      try {
        setLoading(true);
        await axios.post(`${process.env.REACT_APP_API_URL}interviews/reschedule`, {
          interviewId,
          interviewDate: pendingValues.date,
          interviewTime: pendingValues.time,
          location: pendingValues.location,
          meetingLink: pendingValues.meetingLink,
          additionalNotes: pendingValues.additionalNotes
        });
        setSuccessMessage("Interview rescheduled successfully!");
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate("/interviews");
        }, 2000);
      } catch (error: any) {
        console.log(error);
        setFailureMessage(error.response?.data?.error || "Failed to reschedule interview.");
        setShowFailureModal(true);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Container className="mt-5">
      <ToastContainer />
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Reschedule Interview</h4>
            </Card.Header>
            <Card.Body>
              {loading && <div className="alert alert-info">Loading...</div>}
              {interviewDetails && (
                <form onSubmit={formik.handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">New Date</label>
                    <input
                      type="date"
                      className={`form-control ${formik.errors.date && formik.touched.date ? 'is-invalid' : ''}`}
                      name="date"
                      value={formik.values.date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur} 
                    />
                    {formik.touched.date && formik.errors.date && 
                      <div className="invalid-feedback">{formik.errors.date}</div>
                    }
                  </div>
                  <div className="mb-3">
                    <label className="form-label">New Time</label>
                    <input
                      type="time"
                      className={`form-control ${formik.errors.time && formik.touched.time ? 'is-invalid' : ''}`}
                      name="time"
                      value={formik.values.time}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.time && formik.errors.time && 
                      <div className="invalid-feedback">{formik.errors.time}</div>
                    }
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Location</label>
                    <input
                      type="text"
                      className={`form-control ${formik.errors.location && formik.touched.location ? 'is-invalid' : ''}`}
                      name="location"
                      value={formik.values.location}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.location && formik.errors.location && 
                      <div className="invalid-feedback">{formik.errors.location}</div>
                    }
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Meeting Link (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="meetingLink"
                      value={formik.values.meetingLink}
                      onChange={formik.handleChange}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Additional Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      name="additionalNotes"
                      value={formik.values.additionalNotes}
                      onChange={formik.handleChange}
                      rows={3}
                    />
                  </div> 

                   <Row className="mt-4">
                    <Col className="text-center">
                      {/* CHANGE: Disabled button during loading */}
                      <Button 
                        type="submit" 
                        variant="success" 
                        disabled={loading || !formik.isValid}
                      >
                        {loading ? "Rescheduling..." : "Reschedule Interview"}
                      </Button>
                      {/* CHANGE: Disabled button during loading */}
                      <Button 
                        variant="danger" 
                        className="mx-2" 
                        onClick={() => navigate("/interviews")}
                        disabled={loading}
                      >
                        Go Back
                      </Button>
                    </Col>
                  </Row>
                </form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <ConfirmationModal
        show={modalShow}
        modalMessage={modalMessage}
        onHide={() => setModalShow(false)}
        confirmation={handleConfirmation}
        operationType="Confirm"
      />
       <NewconfirmationModal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} modalMessage={successMessage} operationType="Ok" timeoutDuration={3000} />
       <AllFailureModal show={showFailureModal} onHide={() => setShowFailureModal(false)} modalMessage={failureMessage}  operationType="Ok" timeoutDuration={5000}/>
    </Container>
  );
};

export default InterviewReschedule;
